import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { DayPicker, type DateRange } from "react-day-picker";
import "react-day-picker/style.css";
import {
  CalendarClock,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  Download,
  GitCompareArrows,
  Layers,
  Presentation,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog } from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { cn } from "@/lib/utils";
import { formatDateRange } from "@/lib/format";
import { handleTabListArrowKeys } from "@/lib/tabListKeyboard";
import { useClientBySlug } from "./useClients";
import { downloadClientWorkbook, recordClientView, type ExportScope } from "./api";
import {
  usePeriods,
  useDeletePeriod,
  useActiveUploadJob,
  type SelectedView,
} from "../periods/usePeriods";
import { applyViewParams, decodeViewParams, encodeViewParams } from "../present/viewParams";
import { UploadPeriodDialog } from "../periods/UploadPeriodDialog";
import { ExecutiveSummaryTab } from "./tabs/ExecutiveSummary";
import { TeamPerformanceTab } from "./tabs/TeamPerformance";
import { InactiveTeamsTab } from "./tabs/InactiveTeams";
import { TopUsersTab } from "./tabs/TopUsers";
import { CheckinActivityTab } from "./tabs/CheckinActivity";
import { EmergencyActivityTab } from "./tabs/EmergencyActivity";
import { TeamAlertActivityTab } from "./tabs/TeamAlertActivity";
import { DeviceTypeAnalysisTab } from "./tabs/DeviceTypeAnalysis";
import { AppUsageTab } from "./tabs/AppUsage";
import { MonthlyTrendsTab } from "./tabs/MonthlyTrends";
import { TierSettingsDialog } from "./TierSettingsDialog";
import { ClientDetailsTab, formatARR } from "./tabs/ClientDetails";
import { ClientNotesTab } from "./tabs/ClientNotes";
import { ClientSurveyTab } from "./tabs/ClientSurvey";
import type { client, reportingPeriod } from "@duress/shared";

function toDateStr(date: Date): string {
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

const CLIENT_TABS = [
  { id: "client-details", label: "Details" },
  { id: "client-notes", label: "Notes" },
  { id: "client-survey", label: "Survey" },
] as const;

const USAGE_TABS = [
  { id: "executive-summary", label: "Executive Summary" },
  { id: "team-performance", label: "Team Performance" },
  { id: "inactive-teams", label: "Inactive Teams" },
  { id: "top-users", label: "Top Users" },
  { id: "device-type-analysis", label: "Device Type Analysis" },
  { id: "monthly-trends", label: "Monthly Trends" },
  { id: "app-usage", label: "App Usage" },
] as const;

// The six device-fleet tabs above are meaningless for an App-Only client --
// there's no session/fleet CSV to derive them from -- so they're swapped out
// entirely for App Usage rather than left to render empty. A mixed client
// (devices + app users) keeps the device tabs as-is (Network Adoption on
// Executive Summary stays device-derived, feeding the Health Score exactly as
// before) and always gets App Usage appended as an extra tab, whether or not
// a roster has been uploaded yet -- AppUsageTab itself renders the upload
// dropzone when there's no roster (see AppUsage.tsx's "not-uploaded" state).
// Previously this was gated on `hasAppUsers`, which meant a freshly-created
// mixed client had no way to reach the roster upload UI at all until a
// device-usage CSV existed first. See project-management for the App-Only
// usage design.
function getVisibleUsageTabs(appOnly: boolean | null): readonly (typeof USAGE_TABS)[number][] {
  if (appOnly) return USAGE_TABS.filter((t) => t.id === "app-usage");
  return USAGE_TABS;
}

const FEATURE_ACTIVITY_TABS = [
  { id: "emergency-activity", label: "Emergency Activity" },
  { id: "checkin-activity", label: "Check-in Activity" },
  { id: "team-alert-activity", label: "Team Alert Activity" },
] as const;

const ALL_TABS = [...CLIENT_TABS, ...USAGE_TABS, ...FEATURE_ACTIVITY_TABS] as const;
type TabId = (typeof ALL_TABS)[number]["id"];
const DEFAULT_TAB: TabId = "executive-summary";

function isTabId(value: string): value is TabId {
  return ALL_TABS.some((t) => t.id === value);
}

const SECTIONS = [
  { id: "client" as const, label: "Client Info", tabs: CLIENT_TABS },
  { id: "usage" as const, label: "Usage", tabs: USAGE_TABS },
  { id: "feature-activity" as const, label: "Feature Activity", tabs: FEATURE_ACTIVITY_TABS },
];
type SectionId = "client" | "usage" | "feature-activity";

function getSectionForTab(tabId: TabId): SectionId {
  if (CLIENT_TABS.some((t) => t.id === tabId)) return "client";
  if (USAGE_TABS.some((t) => t.id === tabId)) return "usage";
  return "feature-activity";
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatPeriodLabel(p: reportingPeriod.ReportingPeriod): string {
  const monthName = MONTH_NAMES[p.month - 1] ?? String(p.month);
  return `${monthName} ${String(p.year)}`;
}

function describeView(
  view: SelectedView | null,
  periods: reportingPeriod.ReportingPeriod[],
): string | null {
  if (!view) return null;
  if (view.mode === "alltime") return "All time";
  if (view.mode === "range") return formatDateRange(view.from, view.to);
  const p = periods.find((period) => period.id === view.periodId);
  return p ? formatPeriodLabel(p) : null;
}

// A switch (rather than a ternary chain) so TypeScript flags it at compile
// time if a tab is ever added to ALL_TABS without a render case here.
function renderTabContent(
  tabId: TabId,
  clientData: client.ClientListItem,
  selectedView: SelectedView | null,
  hasPeriods: boolean,
  compareView: SelectedView | null,
  compareLabel: string | null,
): React.JSX.Element {
  switch (tabId) {
    case "client-details":
      return <ClientDetailsTab client={clientData} />;
    case "client-notes":
      return <ClientNotesTab clientId={clientData.id} />;
    case "client-survey":
      return <ClientSurveyTab clientId={clientData.id} />;
    case "executive-summary":
      // App-Only clients land here by default (it's DEFAULT_TAB) before ever
      // picking a tab -- show App Usage instead of a device-fleet
      // summary that has nothing to summarise.
      if (clientData.appOnly === true) {
        return <AppUsageTab clientId={clientData.id} />;
      }
      return (
        <ExecutiveSummaryTab
          clientId={clientData.id}
          view={selectedView}
          compareView={compareView}
          compareLabel={compareLabel}
          healthScore={clientData.healthScore}
          prevHealthScore={clientData.previousHealthScore}
        />
      );
    case "app-usage":
      return <AppUsageTab clientId={clientData.id} />;
    case "team-performance":
      return <TeamPerformanceTab clientId={clientData.id} view={selectedView} />;
    case "inactive-teams":
      return <InactiveTeamsTab clientId={clientData.id} view={selectedView} />;
    case "top-users":
      return <TopUsersTab clientId={clientData.id} view={selectedView} />;
    case "checkin-activity":
      return <CheckinActivityTab clientId={clientData.id} view={selectedView} />;
    case "emergency-activity":
      return <EmergencyActivityTab clientId={clientData.id} view={selectedView} />;
    case "team-alert-activity":
      return <TeamAlertActivityTab clientId={clientData.id} view={selectedView} />;
    case "device-type-analysis":
      return <DeviceTypeAnalysisTab
          clientId={clientData.id}
          clientSlug={clientData.slug}
          view={selectedView}
        />;
    case "monthly-trends":
      return <MonthlyTrendsTab clientId={clientData.id} />;
    default: {
      const _exhaustive: never = tabId;
      void _exhaustive;
      return (
        <EmptyTabContent
          tab={ALL_TABS.find((t) => t.id === tabId)?.label ?? ""}
          hasPeriods={hasPeriods}
        />
      );
    }
  }
}

export function ClientDetailPage(): React.JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  const location = useLocation();
  const { data: client, isLoading, error, refetch } = useClientBySlug(slug);

  // Tab, selected view and compare view all live in the URL rather than
  // component state, so a link to a specific tab/period is shareable, a
  // refresh (or an OAuth bounce) doesn't lose your place, and Back steps
  // between tabs instead of leaving the client page entirely.
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get("tab");
  const activeTab: TabId = rawTab && isTabId(rawTab) ? rawTab : DEFAULT_TAB;
  const setActiveTab = (tab: TabId): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === DEFAULT_TAB) next.delete("tab");
      else next.set("tab", tab);
      return next;
    });
  };

  // null until the user picks a view; the effective default is derived below
  // once the period list has loaded.
  const selectedView: SelectedView | null = decodeViewParams(searchParams);
  const setSelectedView = (view: SelectedView | null): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        applyViewParams(next, view);
        return next;
      },
      { replace: true },
    );
  };

  const compareView: SelectedView | null = decodeViewParams(searchParams, "compare");
  const setCompareView = (view: SelectedView | null): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        applyViewParams(next, view, "compare");
        return next;
      },
      { replace: true },
    );
  };

  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    const state = location.state as { openUpload?: boolean } | null;
    if (state?.openUpload) {
      setUploadOpen(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);
  const [tierSettingsOpen, setTierSettingsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [renewalDismissed, setRenewalDismissed] = useState(false);

  const { data: periodsData, isLoading: periodsLoading } = usePeriods(client?.id ?? "");
  // Surfaces an in-flight usage upload (from an earlier dialog instance, a
  // different tab, or before this page was refreshed) so it's visible
  // without having to reopen the upload dialog to find out.
  const activeUploadJob = useActiveUploadJob(client?.id ?? "");
  const periods = periodsData?.items ?? [];

  // Default to the newest period rather than All time. All time is the single
  // most expensive view to compute (it spans every session a client has ever
  // uploaded), and it was previously the landing view for every client, so
  // simply opening a page paid for it. The latest month is what people look at
  // first anyway, and it reads one already-computed period row. The list comes
  // back newest-first, so items[0] is the latest. Derived rather than set in an
  // effect so there is no render where a view is selected but not yet applied.
  const effectiveView: SelectedView | null =
    selectedView ?? (periods[0] ? { mode: "period", periodId: periods[0].id } : null);

  // Default comparison target: the period immediately before the one being
  // viewed. `periods` is newest-first (see above), so that's the next entry
  // in the array. Only defined for period-mode views — a custom range or
  // "All time" has no single well-defined "previous period" to fall back to.
  // An explicit `compareView` pick always overrides this.
  const autoCompareView: SelectedView | null =
    effectiveView?.mode === "period"
      ? (() => {
          const idx = periods.findIndex((p) => p.id === effectiveView.periodId);
          const prev = idx >= 0 ? periods[idx + 1] : undefined;
          return prev ? { mode: "period" as const, periodId: prev.id } : null;
        })()
      : null;
  const effectiveCompareView = compareView ?? autoCompareView;

  // Record this visit for the "recently viewed" feature.
  useEffect(() => {
    if (client?.id) void recordClientView(client.id);
  }, [client?.id]);

  if (isLoading) {
    return <LoadingSkeleton variant="text" />;
  }

  if (error || !client) {
    return (
      <ErrorCard message="Could not load client. Check the URL and try again." onRetry={refetch} />
    );
  }

  const canExport = periods.length > 0;
  const exportScope: ExportScope = (() => {
    if (effectiveView?.mode === "period") return { periodId: effectiveView.periodId };
    if (effectiveView?.mode === "range") return { from: effectiveView.from, to: effectiveView.to };
    return {};
  })();

  const handleExport = async (): Promise<void> => {
    if (!exportScope) return;
    setExporting(true);
    setExportError(null);
    try {
      await downloadClientWorkbook(client.slug, exportScope);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="-m-6">
      {/* Header bar, renewal banner and tab nav stick together just below the
          app header (top-16 = AppShell's h-16) so the period picker, Export,
          Present and the section/tab row stay reachable while a long tab
          (e.g. Team Performance for a large client) scrolls underneath.
          bg-card here is required, not decorative: the export-error and
          renewal banners use translucent backgrounds (bg-emergency-red/10
          etc.), and without an opaque surface behind them the tab content
          scrolling underneath shows through and overlaps the banner text. */}
      <div className="sticky top-16 z-10 bg-card">
      {/* Header bar */}
      <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between gap-4 flex-shrink-0">
        <div className="min-w-0 flex items-center gap-3">
          <div className="min-w-0">
            <h1 className="min-w-0 max-w-[340px] text-2xl font-head font-semibold text-foreground line-clamp-2 leading-tight break-words">
              {client.name}
            </h1>
            {client.arr != null ? (
              <p className="text-xs text-muted-foreground mt-0.5">ARR {formatARR(client.arr)}</p>
            ) : null}
          </div>
          <Link
            to={`/compare?a=${client.slug}`}
            className="flex flex-shrink-0 items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <GitCompareArrows className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="hidden sm:inline">Compare</span>
          </Link>
          {periodsLoading ? (
            <span className="text-sm text-muted-foreground px-2">Loading periods…</span>
          ) : effectiveView === null ? (
            <span className="text-sm text-muted-foreground px-2">No periods uploaded yet</span>
          ) : (
            <PeriodPicker
              periods={periods}
              selectedView={effectiveView}
              onSelect={setSelectedView}
              clientId={client.id}
              onDeleted={(periodId) => {
                // A deleted period can't stay selected -- fall back to the
                // (now newest-first-excluding-it) default rather than an
                // effectiveView that still points at a period.id nothing has
                // any more.
                if (selectedView?.mode === "period" && selectedView.periodId === periodId) {
                  setSelectedView(null);
                }
                if (compareView?.mode === "period" && compareView.periodId === periodId) {
                  setCompareView(null);
                }
              }}
            />
          )}
          {activeTab === "executive-summary" && effectiveCompareView !== null && (
            <div className="flex items-center">
              <span className="text-xs text-muted-foreground pl-1">vs</span>
              <PeriodPicker
                periods={periods}
                selectedView={effectiveCompareView}
                onSelect={setCompareView}
                variant="compact"
              />
              {compareView !== null && (
                <button
                  type="button"
                  onClick={() => {
                    setCompareView(null);
                  }}
                  aria-label="Reset to previous period"
                  className="text-muted-foreground hover:text-foreground p-1"
                  title="Reset to previous period"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setUploadOpen(true);
            }}
            className="gap-1 text-xs text-muted-foreground hover:text-foreground px-2 h-8"
            aria-label={activeUploadJob.jobId ? "Upload in progress" : "Upload period"}
            title={
              activeUploadJob.jobId
                ? "An upload is still processing in the background — click to view progress"
                : undefined
            }
          >
            <Upload className={cn("h-3 w-3", activeUploadJob.jobId && "animate-pulse")} />
            {activeUploadJob.jobId ? "Upload in progress…" : "Upload Period"}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setTierSettingsOpen(true);
            }}
            className="gap-1.5"
            aria-label="Tier settings"
          >
            <Layers className="h-3.5 w-3.5" />
            Tiers
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void handleExport();
            }}
            disabled={!canExport || exporting}
            className="gap-1.5"
            aria-label="Export to Excel"
            title={
              exportError ??
              (canExport
                ? "Export the selected period to Excel"
                : "Upload a period to enable export")
            }
          >
            <Download className="h-3.5 w-3.5" />
            {exporting ? "Exporting…" : "Export"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Opens in a new tab so a presenter can screen-share just this
              // window without exposing the rest of Vitals in a meeting.
              const params = effectiveView ? encodeViewParams(effectiveView) : null;
              const url = `/clients/${client.slug}/present${params ? `?${params.toString()}` : ""}`;
              window.open(url, "_blank", "noopener,noreferrer");
            }}
            className="gap-1.5"
            aria-label="Present"
          >
            <Presentation className="h-3.5 w-3.5" />
            Present
          </Button>
        </div>
      </div>

      {/* Export error — was previously only surfaced in the button's title
          tooltip, so a failed export showed the user nothing. */}
      {exportError && (
        <div className="flex items-center justify-between gap-3 border-b border-destructive/30 bg-destructive/10 px-6 py-2.5 flex-shrink-0">
          <p className="text-sm font-medium text-pill-red">{exportError}</p>
          <button
            type="button"
            onClick={() => {
              setExportError(null);
            }}
            aria-label="Dismiss export error"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Renewal banner */}
      {!renewalDismissed &&
        client.contractEndDate &&
        (() => {
          const end = new Date(client.contractEndDate + "T00:00:00");
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const days = Math.ceil((end.getTime() - now.getTime()) / 86_400_000);
          if (days < 0 || days > 90) return null;
          const label = days === 0 ? "today" : days === 1 ? "tomorrow" : `in ${String(days)} days`;
          const [, m, d] = client.contractEndDate.split("-");
          const months = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const dateLabel = `${String(parseInt(d ?? "1", 10))} ${months[parseInt(m ?? "1", 10) - 1] ?? ""}`;
          const urgentBg =
            days <= 14
              ? "bg-emergency-red/10 border-emergency-red/30"
              : "bg-amber-50 border-amber-300 dark:bg-amber-500/15 dark:border-amber-500/40";
          const urgentIcon = days <= 14 ? "text-pill-red" : "text-amber-500";
          const urgentText = days <= 14 ? "text-pill-red" : "text-foreground";
          const urgentLink =
            days <= 14 ? "text-pill-red" : "text-amber-700 dark:text-amber-400";
          return (
            <div
              className={`flex items-center justify-between gap-3 border-b px-6 py-2.5 flex-shrink-0 ${urgentBg}`}
            >
              <div className="flex items-center gap-2">
                <CalendarClock className={`h-4 w-4 flex-shrink-0 ${urgentIcon}`} />
                <p className={`text-sm font-medium ${urgentText}`}>
                  Contract renews {label} — {dateLabel}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab("client-details");
                  }}
                  className={`text-xs font-medium underline underline-offset-2 ${urgentLink} hover:no-underline`}
                >
                  Update date
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRenewalDismissed(true);
                  }}
                  aria-label="Dismiss renewal notice"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })()}

      <div className="bg-card border-b border-border">
        <TwoLevelNav activeTab={activeTab} onSelect={setActiveTab} appOnly={client.appOnly} />
      </div>
      </div>

      {/* Body: tab content flows normally so the page (not an inner pane)
          scrolls — the block above stays pinned via `sticky`. */}
      <div className="p-6" role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
        {renderTabContent(
          activeTab,
          client,
          effectiveView,
          periods.length > 0,
          effectiveCompareView,
          describeView(effectiveCompareView, periods),
        )}
      </div>

      <UploadPeriodDialog
        clientId={client.id}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={(id) => {
          setSelectedView({ mode: "period", periodId: id });
        }}
      />

      <TierSettingsDialog
        clientId={client.id}
        open={tierSettingsOpen}
        onOpenChange={setTierSettingsOpen}
      />
    </div>
  );
}

// Tracks whether the secondary tab row has more content scrolled off to
// either side, so the overflow fade below only shows on the edge(s) that
// actually have something to reveal (UI-21) -- re-measures on scroll, on
// resize, and whenever the visible tab set changes (switching sections can
// flip a row from fitting to overflowing or back).
function useScrollEdges(
  ref: React.RefObject<HTMLDivElement | null>,
  dep: unknown,
): { canScrollLeft: boolean; canScrollRight: boolean } {
  const [edges, setEdges] = useState({ canScrollLeft: false, canScrollRight: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = (): void => {
      setEdges({
        canScrollLeft: el.scrollLeft > 1,
        canScrollRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      });
    };
    update();
    el.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
    // `dep` isn't read in the effect body -- it's an intentional extra
    // trigger to re-measure after the tab set changes (e.g. switching
    // sections can flip this row from fitting to overflowing or back).
  }, [ref, dep]);

  return edges;
}

function TwoLevelNav({
  activeTab,
  onSelect,
  appOnly,
}: {
  activeTab: TabId;
  onSelect: (id: TabId) => void;
  appOnly: boolean | null;
}): React.JSX.Element {
  // executive-summary is DEFAULT_TAB, so an App-Only client can land here
  // before ever picking a tab (renderTabContent substitutes its content for
  // App Usage in that case) -- treat it as App Usage for
  // nav-highlighting purposes too, so the tab bar doesn't show nothing
  // selected.
  const displayTab: TabId =
    appOnly === true && activeTab === "executive-summary" ? "app-usage" : activeTab;
  const activeSection = getSectionForTab(displayTab);
  const tabRowRef = useRef<HTMLDivElement>(null);

  const handleSectionClick = (sectionId: SectionId): void => {
    if (sectionId === "usage") {
      const firstTab = getVisibleUsageTabs(appOnly)[0];
      if (firstTab) onSelect(firstTab.id);
      return;
    }
    const section = SECTIONS.find((s) => s.id === sectionId);
    const firstTab = section?.tabs[0];
    if (firstTab) onSelect(firstTab.id);
  };

  const activeTabs =
    activeSection === "usage"
      ? getVisibleUsageTabs(appOnly)
      : (SECTIONS.find((s) => s.id === activeSection)?.tabs ?? []);
  const { canScrollLeft, canScrollRight } = useScrollEdges(tabRowRef, activeSection);

  return (
    <div>
      {/* Primary: section selector */}
      <div className="flex items-center gap-0 px-4 border-b border-border" role="tablist" aria-label="Section">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            type="button"
            role="tab"
            data-value={section.id}
            aria-selected={activeSection === section.id}
            tabIndex={activeSection === section.id ? 0 : -1}
            onClick={() => {
              handleSectionClick(section.id);
            }}
            onKeyDown={(e) => { handleTabListArrowKeys(e, (id) => { handleSectionClick(id as SectionId); }); }}
            className={cn(
              "px-4 py-3 text-sm font-head font-semibold border-b-2 transition-colors whitespace-nowrap -mb-px",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-inset",
              activeSection === section.id
                ? "border-phoenix-orange text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-phoenix-orange/40",
            )}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Secondary: tabs within active section */}
      <div className="relative">
        <div
          ref={tabRowRef}
          className="flex items-center gap-0 px-4 overflow-x-auto"
          role="tablist"
          aria-label="Tab"
        >
          {activeTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              data-value={tab.id}
              id={`tab-${tab.id}`}
              aria-controls={`tabpanel-${tab.id}`}
              aria-selected={displayTab === tab.id}
              tabIndex={displayTab === tab.id ? 0 : -1}
              onClick={() => {
                onSelect(tab.id);
              }}
              onKeyDown={(e) => { handleTabListArrowKeys(e, (id) => { onSelect(id as TabId); }); }}
              className={cn("tab-btn", displayTab === tab.id && "tab-btn-active")}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {canScrollLeft && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-card to-transparent"
          />
        )}
        {canScrollRight && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card to-transparent"
          />
        )}
      </div>
    </div>
  );
}

function PeriodPicker({
  periods,
  selectedView,
  onSelect,
  variant = "default",
  clientId,
  onDeleted,
}: {
  periods: reportingPeriod.ReportingPeriod[];
  selectedView: SelectedView | null;
  onSelect: (view: SelectedView) => void;
  /** "compact" drops the border and calendar icon — for a picker that annotates
   * another control (e.g. the comparison period) rather than standing on its own. */
  variant?: "default" | "compact";
  /** Omitted for the compact/comparison picker -- deleting a period from there
   * would be deleting it everywhere, which isn't a "comparison" action. Present
   * only means "this instance offers delete", not "this client has one". */
  clientId?: string;
  /** Called after a period is actually removed, so the parent can drop it from
   * any selection state that still points at its id. */
  onDeleted?: (periodId: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<"list" | "calendar">("list");
  const [rangeSelection, setRangeSelection] = useState<DateRange | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<reportingPeriod.ReportingPeriod | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const deletePeriod = useDeletePeriod(clientId ?? "");

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    const periodId = deleteTarget.id;
    await deletePeriod.mutateAsync(periodId);
    setDeleteTarget(null);
    onDeleted?.(periodId);
  };

  useEffect(() => {
    const handleMouse = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setPanel("list");
      }
    };
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        setPanel("list");
      }
    };
    document.addEventListener("mousedown", handleMouse);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleMouse);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  // Focuses the current selection (or the first option) as soon as the list
  // opens, so arrow-key navigation works immediately -- without this, a
  // keyboard user would need an extra Tab press just to reach the first
  // option before Up/Down did anything.
  useEffect(() => {
    if (!open || panel !== "list") return;
    const container = ref.current;
    if (!container) return;
    const options = container.querySelectorAll<HTMLButtonElement>('[role="option"]');
    const selected = container.querySelector<HTMLButtonElement>(
      '[role="option"][aria-selected="true"]',
    );
    (selected ?? options[0])?.focus();
  }, [open, panel]);

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const list = e.currentTarget.closest('[role="listbox"]');
    if (!list) return;
    const options = Array.from(list.querySelectorAll<HTMLButtonElement>('[role="option"]'));
    const currentIndex = options.indexOf(e.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex: number;
    if (e.key === "ArrowDown") nextIndex = Math.min(currentIndex + 1, options.length - 1);
    else if (e.key === "ArrowUp") nextIndex = Math.max(currentIndex - 1, 0);
    else if (e.key === "Home") nextIndex = 0;
    else nextIndex = options.length - 1;

    e.preventDefault();
    options[nextIndex]?.focus();
  };

  const triggerLabel = (): string => describeView(selectedView, periods) ?? "Select period";

  const handleRangeSelect = (range: DateRange | undefined): void => {
    setRangeSelection(range);
    // react-day-picker v10 sets to === from on the first click; only apply once both
    // ends are selected and they are different days.
    if (range?.from && range.to && range.from.getTime() !== range.to.getTime()) {
      onSelect({ mode: "range", from: toDateStr(range.from), to: toDateStr(range.to) });
      setOpen(false);
      setPanel("list");
      setRangeSelection(undefined);
    }
  };

  const rowClass = (active: boolean): string =>
    `w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
      active
        ? "bg-accent text-accent-foreground font-medium"
        : "text-foreground hover:bg-accent hover:text-accent-foreground"
    }`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select view"
        className={
          variant === "compact"
            ? "flex items-center gap-1 h-9 rounded-md px-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            : "flex items-center gap-1.5 h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        }
      >
        {variant === "default" && (
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
        <span>{triggerLabel()}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 z-50 rounded-md border border-border bg-card shadow-md overflow-hidden">
          {panel === "list" ? (
            <div className="w-52 p-1" role="listbox" aria-label="Periods">
              <button
                type="button"
                role="option"
                aria-selected={selectedView?.mode === "alltime"}
                onClick={() => {
                  onSelect({ mode: "alltime" });
                  setOpen(false);
                }}
                onKeyDown={handleOptionKeyDown}
                className={rowClass(selectedView?.mode === "alltime")}
              >
                All time
              </button>
              <div className="my-1 border-t border-border" aria-hidden="true" />
              <div className="max-h-52 overflow-y-auto">
                {periods.map((p) => {
                  const isActive =
                    selectedView?.mode === "period" && selectedView.periodId === p.id;
                  return (
                    <div key={p.id} className="relative flex items-center">
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => {
                          onSelect({ mode: "period", periodId: p.id });
                          setOpen(false);
                        }}
                        onKeyDown={handleOptionKeyDown}
                        className={`${rowClass(isActive)} flex-1 ${clientId ? "pr-7" : ""}`}
                      >
                        {formatPeriodLabel(p)}
                      </button>
                      {clientId && (
                        <button
                          type="button"
                          aria-label={`Delete ${formatPeriodLabel(p)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(p);
                          }}
                          className="absolute right-1.5 rounded-sm p-1 text-muted-foreground/70 transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="my-1 border-t border-border" aria-hidden="true" />
              <button
                type="button"
                role="option"
                aria-selected={selectedView?.mode === "range"}
                onClick={() => {
                  setPanel("calendar");
                }}
                onKeyDown={handleOptionKeyDown}
                className={rowClass(selectedView?.mode === "range")}
              >
                {selectedView?.mode === "range"
                  ? formatDateRange(selectedView.from, selectedView.to)
                  : "Custom range…"}
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 px-3 pt-3 pb-1">
                <button
                  type="button"
                  onClick={() => {
                    setPanel("list");
                    setRangeSelection(undefined);
                  }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <ChevronLeft className="h-3 w-3" />
                  Back
                </button>
                <span className="text-xs text-muted-foreground ml-auto">Select a date range</span>
              </div>
              <DayPicker mode="range" selected={rangeSelection} onSelect={handleRangeSelect} />
            </div>
          )}
        </div>
      )}
      {clientId && (
        <AlertDialog
          open={deleteTarget !== null}
          onOpenChange={(next) => {
            if (!next) setDeleteTarget(null);
          }}
          title={deleteTarget ? `Delete ${formatPeriodLabel(deleteTarget)}?` : ""}
          description="This removes the period and its uploaded sessions. Team and device history stay -- re-uploading a fuller CSV for this month brings it right back."
          confirmLabel="Delete period"
          onConfirm={() => void handleConfirmDelete()}
          isPending={deletePeriod.isPending}
        />
      )}
    </div>
  );
}

function EmptyTabContent({
  tab,
  hasPeriods,
}: {
  tab: string;
  hasPeriods: boolean;
}): React.JSX.Element {
  return (
    <EmptyState
      title={tab}
      body={
        hasPeriods
          ? "This tab is coming soon."
          : "Upload a reporting period to see data here."
      }
    />
  );
}
